import type { ReactNode } from 'react';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { JobBillingStateBadge } from '@/components/billing/JobBillingStateBadge';
import type { ToBillRow } from '@jobos/shared';

/**
 * Stage 1's rows are JOBS, not invoices — they are the work that has not been
 * billed yet. Two row components in one shell; they are not contorted into a
 * single type (spec §6).
 */

export interface ToBillTableProps {
  rows: ToBillRow[];
  onRowClick?: (row: ToBillRow) => void;
  /** The primary action, rendered per row. Opens the existing invoice dialogs. */
  actionFor?: (row: ToBillRow) => ReactNode;
  emptyState?: ReactNode;
}

/**
 * The uninvoiced remainder is the number that makes the row actionable — it is
 * what is left to bill. It is null, not zero, when the job has no active
 * breakdown document: "0 left to bill" would say the job is finished when in
 * fact its value is simply unknown.
 */
function Uninvoiced({ row }: { row: ToBillRow }) {
  if (row.uninvoiced === null) {
    return <span className="text-xs text-gray-400">No breakdown</span>;
  }
  return (
    <span className="whitespace-nowrap font-medium tabular-nums text-blue-900">
      {formatCurrency(row.uninvoiced)}
    </span>
  );
}

export function ToBillTable({ rows, onRowClick, actionFor, emptyState }: ToBillTableProps) {
  if (rows.length === 0 && emptyState) return <>{emptyState}</>;

  return (
    <div className="overflow-x-auto rounded-lg border bg-white">
      <table className="w-full min-w-[44rem] text-sm">
        <thead>
          <tr className="border-b bg-gray-50 text-left text-gray-500">
            <th className="px-4 py-3 font-medium">Job</th>
            <th className="px-4 py-3 font-medium">Client</th>
            <th className="whitespace-nowrap px-4 py-3 font-medium">Completed</th>
            <th className="whitespace-nowrap px-4 py-3 text-right font-medium">Contract value</th>
            <th className="whitespace-nowrap px-4 py-3 text-right font-medium">Invoiced</th>
            <th className="whitespace-nowrap px-4 py-3 text-right font-medium">Left to bill</th>
            <th className="px-4 py-3 font-medium">State</th>
            <th className="w-40 px-2 py-3" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.jobId}
              className={cn(
                'border-b last:border-b-0',
                onRowClick && 'cursor-pointer hover:bg-gray-50',
              )}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              <td className="max-w-[16rem] px-4 py-3">
                <span className="font-mono">{row.jobCode}</span>
                {row.jobDescription && (
                  <span className="block truncate text-xs text-gray-500">{row.jobDescription}</span>
                )}
              </td>
              <td className="max-w-[12rem] truncate px-4 py-3">{row.clientName}</td>
              <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                {row.completedDate ? formatDate(row.completedDate) : '—'}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">
                {row.contractValue === null ? (
                  <span className="text-gray-400">—</span>
                ) : (
                  formatCurrency(row.contractValue)
                )}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(row.invoiced)}</td>
              <td className="px-4 py-3 text-right">
                <Uninvoiced row={row} />
              </td>
              <td className="px-4 py-3">
                <JobBillingStateBadge state={row.billingState} />
              </td>
              <td className="px-2 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                {actionFor?.(row)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface ToBillCardsProps extends ToBillTableProps {}

export function ToBillCards({ rows, onRowClick, actionFor, emptyState }: ToBillCardsProps) {
  if (rows.length === 0 && emptyState) return <>{emptyState}</>;

  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <div
          key={row.jobId}
          className={cn('rounded-lg border bg-white p-3', onRowClick && 'active:bg-gray-50')}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-mono text-sm">{row.jobCode}</p>
              <p className="truncate text-sm text-gray-700">{row.clientName}</p>
              {row.jobDescription && (
                <p className="truncate text-xs text-gray-500">{row.jobDescription}</p>
              )}
            </div>
            <JobBillingStateBadge state={row.billingState} />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-gray-500">
              {formatCurrency(row.invoiced)} invoiced
              {row.contractValue !== null && ` of ${formatCurrency(row.contractValue)}`}
            </span>
            <Uninvoiced row={row} />
          </div>

          {actionFor && (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              {actionFor(row)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** The stage's primary action. Kept here so table and cards render it identically. */
export function CreateInvoiceButton({ onClick }: { onClick: () => void }) {
  return (
    <Button size="sm" onClick={onClick} className="bg-blue-900 text-white hover:bg-blue-800">
      Create invoice
    </Button>
  );
}
